import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, ref, watch } from "vue";
import BaseTable from "@/components-UI/table/BaseTable.vue";
import BaseEditableCard from "@/components-UI/BaseEditableCard.vue";
import BaseChip from "@/components-UI/BaseChip.vue";
import BaseButton from "@/components-UI/BaseButton.vue";
import BaseSelect from "@/components-UI/BaseSelect.vue";
import BaseInputNumber from "@/components-UI/BaseInputNumber.vue";
import DeleteRowTooltip from "@/components/offers/offer/individual-limits/DeleteRowTooltip.vue";
import AddLimitModal from "@/components/offers/offer/individual-limits/AddLimitModal.vue";
import LimitNotification from "@/components/offers/offer/individual-limits/LimitNotification.vue";
import {
    IIndividualLimit,
    INewLimit,
    LimitType,
    LimitPeriod,
    LIMIT_TYPE_LABEL,
    PERIOD_LABEL,
    LIMIT_TYPE_OPTIONS,
    LIMIT_PERIOD_OPTIONS,
    buildLimitId,
    capToRows,
    rowsToPayload,
} from "@/components/offers/offer/individual-limits/types";
import { getAffiliateCaps, setAffiliateCap } from "@/api/offer-access";
import { IAffiliateCap } from "@/api/types/offer-access";
import { getEmployeesAffiliates } from "@/api/user";
import SvgPlus from "@/assets/icons/plus.svg";
import { ITableHeader, showServerError } from "@/utils";

// Бэкенд отдаёт лимиты постранично по партнёрам, а таблица показывает строки-лимиты
// (у партнёра их до шести). Тянем всё и пагинируем строки на клиенте — тогда и счётчик
// «X из Y», и размер страницы считаются в строках, как требует задача.
// FETCH_LIMIT — максимум, который принимает бэкенд affiliate-caps: limit > 100 отдаёт 400.

export default /*#__PURE__*/_defineComponent({
  __name: 'IndividualLimits',
  props: {
    offerId: null,
    currency: { default: '₽' }
  },
  setup(__props: any) {

const props = __props as { offerId: number, currency: string }

const FETCH_LIMIT = 100;
const PAGE_SIZE = 100;



const headers: ITableHeader<keyof IIndividualLimit | 'actions'>[] = [
    { text: 'ID партнёра', value: 'affiliate_id', align: 'center', width: '100px', sortable: false },
    { text: 'Имя партнёра', value: 'affiliate_name', align: 'left', width: 'auto', sortable: false },
    { text: 'Тип лимита', value: 'type', align: 'left', width: 'auto', sortable: false },
    { text: 'Временной промежуток', value: 'period', align: 'left', width: 'auto', sortable: false },
    { text: 'Значение лимита', value: 'value', align: 'right', width: '160px', sortable: false },
    { text: '', value: 'actions', align: 'center', width: '50px', sortable: false },
];

const typeOptions = LIMIT_TYPE_OPTIONS;
const periodOptions = LIMIT_PERIOD_OPTIONS;

const typeLabel = (type: LimitType): string => LIMIT_TYPE_LABEL[type];
const periodLabel = (period: LimitPeriod): string => PERIOD_LABEL[period];

const items = ref<IIndividualLimit[]>([]);
// Пагинация клиентская (is-front-pagination), поэтому total — число строк-лимитов
const total = computed(() => items.value.length);
const isLoading = ref(false);
const isEdit = ref(false);

// Имена партнёров кешируем — GET affiliate-caps возвращает только affiliate_id
const nameCache: Record<number, string> = {};

// Подтягиваем имена партнёров из сервиса пользователей по их id
const fetchAffiliateNames = async (ids: number[]): Promise<void> => {
    const missing = [...new Set(ids)].filter(id => id && !nameCache[id]);
    if (!missing.length) return;
    try {
        const { data } = await getEmployeesAffiliates({
            ids: missing,
            fields: ['id', 'first_name', 'last_name'],
            // limit сервиса пользователей должен быть в диапазоне 20..100000
            limit: Math.min(Math.max(missing.length, 20), 100000),
        });
        (data ?? []).forEach((aff) => {
            if (aff.id) nameCache[aff.id] = [aff.first_name, aff.last_name].filter(Boolean).join(' ');
        });
    } catch {
        // Имена не критичны для отображения лимитов — молча пропускаем
    }
};

// Тянем все страницы партнёров: строки пагинируются на клиенте, поэтому нужен весь набор
const fetchAllCaps = async (): Promise<IAffiliateCap[]> => {
    const caps: IAffiliateCap[] = [];
    let totalCaps = Infinity;
    while (caps.length < totalCaps) {
        const { data, meta } = await getAffiliateCaps({
            offer_legacy_id: props.offerId,
            limit: FETCH_LIMIT,
            offset: caps.length,
        });
        const page = data ?? [];
        caps.push(...page);
        totalCaps = meta?.total ?? caps.length;
        if (!page.length) break;
    }
    return caps;
};

const fetchCaps = async (): Promise<void> => {
    isLoading.value = true;
    try {
        const caps = await fetchAllCaps();
        caps.forEach((cap) => {
            if (cap.affiliate_name) nameCache[cap.affiliate_id] = cap.affiliate_name;
        });
        await fetchAffiliateNames(caps.map(cap => cap.affiliate_id));
        items.value = caps.flatMap((cap) => capToRows(cap, nameCache));
    } catch (err) {
        showServerError(err, 'Не удалось загрузить индивидуальные лимиты');
    } finally {
        isLoading.value = false;
    }
};

onMounted(fetchCaps);

// Снимок для отмены изменений в режиме редактирования
let snapshot: IIndividualLimit[] = [];
const partnerSignature = (rows: IIndividualLimit[]): string =>
    rows.map(r => `${r.type}:${r.period}:${r.value}`).sort().join('|');

const groupByAffiliate = (rows: IIndividualLimit[]): Map<number, IIndividualLimit[]> => {
    const map = new Map<number, IIndividualLimit[]>();
    rows.forEach((row) => {
        const list = map.get(row.affiliate_id) ?? [];
        list.push(row);
        map.set(row.affiliate_id, list);
    });
    return map;
};

// Отмена (кнопка-стрелка): откатываем к снимку, остаёмся в режиме редактирования
const onReset = (): void => {
    if (isEdit.value) {
        items.value = snapshot.map(item => ({ ...item }));
        isEdit.value = false;
    }
};

// Возврат в режим редактирования (ошибка сохранения / провал валидации),
// при котором снимок не пересобираем — чтобы не потерять правки и работу «Отмены».
let isReopening = false;
const reopenEdit = (): void => {
    isReopening = true;
    isEdit.value = true;
};

// У партнёра не может быть двух строк с одинаковыми type+period: в payload они
// схлопнутся в одно поле (last wins), второй тип/период обнулится.
const hasDuplicateTypePeriod = (groups: Map<number, IIndividualLimit[]>): boolean =>
    [...groups.values()].some((rows) => {
        const seen = new Set<string>();
        return rows.some((row) => {
            const key = `${row.type}:${row.period}`;
            if (seen.has(key)) return true;
            seen.add(key);
            return false;
        });
    });

const saveEdits = async (): Promise<void> => {
    const current = groupByAffiliate(items.value);

    if (hasDuplicateTypePeriod(current)) {
        limitNotification.value?.show('У партнёра не может быть двух лимитов с одинаковыми типом и промежутком');
        reopenEdit();
        return;
    }

    const before = groupByAffiliate(snapshot);
    // Партнёры, у которых состав/значения строк изменились, а также те, у кого
    // удалили все строки (есть в before, но нет в current) — им уходит пустой
    // payload (enabled: false), иначе лимиты останутся на бэке.
    const affIds = new Set([...current.keys(), ...before.keys()]);
    const changed = [...affIds].filter(
        affId => partnerSignature(current.get(affId) ?? []) !== partnerSignature(before.get(affId) ?? []),
    );

    if (!changed.length) return;

    isLoading.value = true;
    try {
        await Promise.all(changed.map(affId => setAffiliateCap(rowsToPayload(props.offerId, affId, current.get(affId) ?? []))));
        await fetchCaps();
    } catch (err) {
        showServerError(err, 'Не удалось сохранить изменения');
        // Не теряем несохранённые правки — возвращаемся в режим редактирования
        reopenEdit();
    } finally {
        isLoading.value = false;
    }
};

watch(isEdit, (editing, wasEditing) => {
    if (editing) {
        // Возврат в edit после ошибки/валидации — снимок оставляем прежним
        if (isReopening) {
            isReopening = false;
            return;
        }
        snapshot = items.value.map(item => ({ ...item }));
    } else if (wasEditing) {
        // Выход из режима редактирования по кнопке-галочке — сохраняем изменения
        saveEdits();
    }
});

// Лимит по конверсиям — это количество (целое число, без валюты), остальные — деньги.
const formatValue = (item: IIndividualLimit): string => {
    if (item.type === 'conversion') {
        return item.value.toLocaleString('ru-RU');
    }
    return `${item.value.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${props.currency}`;
};

// Удаление строки = обнуление этого типа/периода у партнёра и повторный POST
const deleteRow = async (row: IIndividualLimit): Promise<void> => {
    // В режиме редактирования удаляем строку только локально: изменения уедут на бэк
    // при сохранении. Иначе немедленный POST + fetchCaps затрут несохранённые правки.
    if (isEdit.value) {
        items.value = items.value.filter(item => item !== row);
        return;
    }
    const remaining = items.value.filter(item =>
        item.affiliate_id === row.affiliate_id && item.id !== row.id);
    isLoading.value = true;
    try {
        await setAffiliateCap(rowsToPayload(props.offerId, row.affiliate_id, remaining));
        await fetchCaps();
    } catch (err) {
        showServerError(err, 'Не удалось удалить лимит');
    } finally {
        isLoading.value = false;
    }
};

const addLimitModal = ref<InstanceType<typeof AddLimitModal> | null>(null);
const limitNotification = ref<InstanceType<typeof LimitNotification> | null>(null);

const addRow = (): void => {
    addLimitModal.value?.openModal();
};

const onAddLimit = async (limit: INewLimit): Promise<void> => {
    nameCache[limit.affiliate_id] = limit.affiliate_name;
    const newRow: IIndividualLimit = {
        id: buildLimitId(limit.affiliate_id, limit.period, limit.type),
        affiliate_id: limit.affiliate_id,
        affiliate_name: limit.affiliate_name,
        type: limit.type,
        period: limit.period,
        value: limit.value,
    };
    isLoading.value = true;
    try {
        // Тянем актуальные лимиты партнёра с бэка: он может быть не на текущей странице
        // (или вовсе вне выборки) — иначе POST затрёт его уже существующие caps.
        const { data } = await getAffiliateCaps({
            offer_legacy_id: props.offerId,
            affiliate_id: limit.affiliate_id,
        });
        const existingRows = (data ?? []).flatMap(cap => capToRows(cap, nameCache));
        const merged = [...existingRows.filter(item => item.id !== newRow.id), newRow];
        await setAffiliateCap(rowsToPayload(props.offerId, limit.affiliate_id, merged));
        await fetchCaps();
    } catch (err) {
        showServerError(err, 'Не удалось добавить лимит');
    } finally {
        isLoading.value = false;
    }
};

return { __sfc: true,FETCH_LIMIT, PAGE_SIZE, props, headers, typeOptions, periodOptions, typeLabel, periodLabel, items, total, isLoading, isEdit, nameCache, fetchAffiliateNames, fetchAllCaps, fetchCaps, snapshot, partnerSignature, groupByAffiliate, onReset, isReopening, reopenEdit, hasDuplicateTypePeriod, saveEdits, formatValue, deleteRow, addLimitModal, limitNotification, addRow, onAddLimit, BaseTable, BaseEditableCard, BaseChip, BaseButton, BaseSelect, BaseInputNumber, DeleteRowTooltip, AddLimitModal, LimitNotification, SvgPlus }
}

})